/**
 * sourceMapLink — the single owner of the /source-map URL contract.
 *
 * The dashboard BUILDS these links (TseDashboard5.handleOpenSourceMap) and the
 * standalone viewer PARSES them (SourceMapPage). Both sides need the same
 * slugification and the same "what is derivable from `file`" rules, so both
 * import from here: if the rules ever change they change once, and a builder
 * that omits a param can never disagree with a page that reconstructs it.
 *
 * v2 key scheme (product-keyed, session-independent):
 *   localStorage key: arta:source-map:v2:<product>:<slug>
 *   URL:              /source-map?product=<p>&file=<f>[&file=<f2>...][&title=<t>]
 *
 * The session id is NOT part of the key or URL because isolateSnapshot strips
 * every session-dependent field — the same product + file always yields the
 * same rendered graph, so one shareable URL and one hot cache key suffice.
 *
 * The URL carries only what the page cannot derive:
 *   - `file`    one per contributing document (required, repeatable)
 *   - `product` required — without it the page cannot fetch anything
 *   - `k`       ONLY when the localStorage slug differs from the one derived
 *               from the first `file` (aggregate cards keyed by card_key)
 *   - `title`   ONLY when it is not just the first file name minus extension
 */

// Document extensions worth stripping for a display title. Deliberately the
// same set scopeGraphToSource.normaliseName strips, so "derivable title" and
// "matchable name" agree about where a filename ends.
const DOC_EXT_RE = /\.(mermaid\.enc|mermaid|enc|pdf|xlsx|xls|docx|doc|md|txt)$/i;

/**
 * slugifySourceKey — the localStorage key's trailing segment. Must stay
 * byte-identical on both sides: the page reconstructs the key the dashboard
 * wrote, and a one-character drift turns the fast handoff into a silent
 * cache miss (visible only as a slower, refetching tab).
 */
export const slugifySourceKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);

/**
 * Full localStorage key (v2, product-keyed). The WRITE side always uses this
 * form. Old session-keyed entries (arta:source-map:<uuid>:<slug>) become
 * unreachable — they are a perf handoff, not data, so the page just falls
 * through to the product-wide fetch.
 */
export const sourceMapKey = (product, slug) =>
  `arta:source-map:v2:${product || ""}:${slug}`;

/**
 * resolveSourceMapKey — the read side, accepting BOTH URL generations:
 *   - legacy: `k` is a whole key (contains ":") -> use verbatim. This keeps
 *     old bookmarks that carried the full key working until the entry expires.
 *   - current: `k` is a bare slug -> compose it against the product param.
 *   - absent: derive the slug from the first `file`, which is what the
 *     dashboard used whenever it chose to omit `k`.
 * Returns "" when there is nothing to look up (caller then skips localStorage).
 */
export const resolveSourceMapKey = (product, kParam, firstFileName) => {
  const k = String(kParam || "").trim();
  if (k.includes(":")) return k;
  const slug = k ? slugifySourceKey(k) : slugifySourceKey(firstFileName);
  if (!slug || !product) return "";
  return sourceMapKey(product, slug);
};

/** Display title derived from a file name: strip directory + one extension. */
export const titleFromFileName = (name) =>
  String(name || "").replace(/^.*[\\/]/, "").replace(DOC_EXT_RE, "").trim();

// Case- and whitespace-insensitive comparison, so "Runbook -  X" and
// "runbook - x.pdf" count as the same title and the param is dropped.
const canonicalTitle = (value) =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

/** True when `title` adds nothing over what the page derives from `fileName`. */
export const isTitleDerivable = (title, fileName) => {
  const derived = canonicalTitle(titleFromFileName(fileName));
  return !!derived && canonicalTitle(title) === derived;
};

/**
 * buildSourceMapUrl — the one place the query string is assembled.
 * Session-free: the URL is /source-map?product=...&file=...
 * `slug` is the slug actually used for the localStorage write, which may come
 * from a card_key and therefore not be derivable from `names[0]`.
 */
export const buildSourceMapUrl = ({ slug, names, product, title }) => {
  const files = (names || []).filter(Boolean);
  const params = new URLSearchParams();
  // `k` only when the page could not arrive at the same slug on its own.
  const derivedSlug = slugifySourceKey(files[0]);
  if (slug && slug !== derivedSlug) params.set("k", slug);
  for (const n of files) params.append("file", n);
  if (product) params.set("product", product);
  if (title && !isTitleDerivable(title, files[0])) params.set("title", title);
  const qs = params.toString();
  return `/source-map${qs ? `?${qs}` : ""}`;
};
